"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, Plus, X } from "lucide-react";

import { cn } from "@/utils/cn";

const avatarVariants = cva("relative flex shrink-0 overflow-hidden rounded-full", {
	variants: { size: { sm: "size-8", default: "size-10", lg: "size-14", xl: "size-16" } },
	defaultVariants: { size: "default" },
});

interface AvatarProps extends React.ComponentProps<typeof AvatarPrimitive.Root>, VariantProps<typeof avatarVariants> {}

function Avatar({ className, size, ...props }: AvatarProps) {
	return <AvatarPrimitive.Root data-slot="avatar" className={cn(avatarVariants({ size, className }))} {...props} />;
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
	return (
		<AvatarPrimitive.Image data-slot="avatar-image" className={cn("aspect-square size-full", className)} {...props} />
	);
}

function AvatarFallback({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
	return (
		<AvatarPrimitive.Fallback
			data-slot="avatar-fallback"
			className={cn("bg-muted flex size-full items-center justify-center rounded-full text-sm font-medium", className)}
			{...props}
		/>
	);
}

// Status indicator variants
const statusVariants = cva("absolute flex items-center justify-center rounded-full border-2 border-background", {
	variants: {
		variant: {
			online: "bg-success-600",
			offline: "bg-neutral-400",
			away: "bg-warning-600",
			busy: "bg-error-600",
			check: "bg-primary text-primary-foreground",
			plus: "bg-neutral-200 text-neutral-600",
			close: "bg-error-600 text-error-foreground",
		},
		position: {
			"top-right": "top-0 right-0",
			"bottom-right": "bottom-0 right-0",
			"top-left": "top-0 left-0",
			"bottom-left": "bottom-0 left-0",
		},
		size: { sm: "size-3", default: "size-4", lg: "size-5" },
	},
	defaultVariants: { variant: "online", position: "top-right", size: "default" },
});

interface AvatarStatusProps extends VariantProps<typeof statusVariants> {
	className?: string;
}

function AvatarStatus({ className, variant, position, size }: AvatarStatusProps) {
	return (
		<div className={cn(statusVariants({ variant, position, size, className }))}>
			{variant === "check" && <Check className="size-2.5" />}
			{variant === "plus" && <Plus className="size-2.5" />}
			{variant === "close" && <X className="size-2.5" />}
		</div>
	);
}

// Avatar Group component
interface AvatarGroupProps extends React.HTMLAttributes<HTMLDivElement> {
	limit?: number;
	total?: number;
}

function AvatarGroup({ children, className, limit = 4, total, ...props }: AvatarGroupProps) {
	const childrenArray = React.Children.toArray(children);
	const visibleChildren = childrenArray.slice(0, limit);
	const remaining = total ? total - limit : childrenArray.length - limit;

	return (
		<div className={cn("flex items-center -space-x-2", className)} {...props}>
			{visibleChildren.map((child, index) => (
				<div key={index} className="relative">
					{child}
				</div>
			))}
			{remaining > 0 && (
				<Avatar className="border-2 border-background bg-primary text-primary-foreground">
					<AvatarFallback>+{remaining}</AvatarFallback>
				</Avatar>
			)}
		</div>
	);
}

export { Avatar, AvatarImage, AvatarFallback, AvatarStatus, AvatarGroup };
